import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface ServiceAreasSectionProps {
  title?: string;
  subtitle?: string;
  ctaCity?: string;
}

const CITIES = [
  { name: 'Houston, TX', icon: 'ri-building-line', desc: 'All neighborhoods & business districts', highlight: true },
  { name: 'Sugar Land, TX', icon: 'ri-map-pin-2-line', desc: 'Fort Bend County & surrounding areas' },
  { name: 'The Woodlands, TX', icon: 'ri-tree-line', desc: 'Montgomery County & surrounding areas' },
  { name: 'Katy, TX', icon: 'ri-home-office-line', desc: 'West Houston & Katy suburb communities' },
  { name: 'Pearland, TX', icon: 'ri-store-2-line', desc: 'Brazoria County & surrounding areas' },
  { name: 'Pasadena, TX', icon: 'ri-building-2-line', desc: 'East Houston & industrial corridor' },
];

export default function ServiceAreasSection({ title = 'Serving Businesses Across Greater Houston', subtitle = 'Prime Facility Services Group provides commercial cleaning and facility services for businesses throughout Houston and surrounding communities.', ctaCity }: ServiceAreasSectionProps) {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="py-24 bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block px-5 py-2 bg-[#C70532]/10 text-[#C70532] text-xs font-bold uppercase tracking-widest rounded-full mb-5">Service Areas</span>
          <h2 className="text-4xl md:text-5xl font-black text-[#03143A] mb-5 leading-tight">{title}</h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">{subtitle}</p>
        </div>

        {/* Full-width map */}
        <div className={`rounded-3xl overflow-hidden mb-12 border border-gray-200 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: '150ms' }}>
          <iframe
            title="Prime Facility Services Group Service Areas — Greater Houston TX"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d221789.7394742903!2d-95.66908!3d29.8162!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640b8b4488d8501%3A0xca12b4ca38d67b6d!2sHouston%2C%20TX!5e0!3m2!1sen!2sus!4v1710000000000!5m2!1sen!2sus"
            width="100%"
            height="420"
            style={{ border: 0, display: 'block' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        {/* City grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          {CITIES.map((city, i) => (
            <div
              key={i}
              className={`flex items-center gap-4 p-5 rounded-2xl border transition-all duration-400 group ${city.highlight ? 'bg-[#03143A] border-[#03143A]' : 'bg-white border-gray-100 hover:border-[#C70532]/30 hover:bg-[#FAF8F5]'} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <div className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${city.highlight ? 'bg-[#C70532]' : 'bg-[#03143A] group-hover:bg-[#C70532] transition-colors'}`}>
                <i className={`${city.icon} text-white text-lg`} />
              </div>
              <div className="flex-1">
                <h4 className={`font-bold text-sm ${city.highlight ? 'text-white' : 'text-[#03143A]'}`}>{city.name}</h4>
                <p className={`text-xs mt-0.5 ${city.highlight ? 'text-white/60' : 'text-gray-400'}`}>{city.desc}</p>
              </div>
              <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 ${city.highlight ? 'bg-white/20' : 'bg-[#C70532]/10'}`}>
                <i className={`ri-check-line text-xs font-bold ${city.highlight ? 'text-white' : 'text-[#C70532]'}`} />
              </div>
            </div>
          ))}
        </div>

        {/* Call to action for service area inquiries */}
        <div className={`text-center p-8 bg-[#03143A] rounded-2xl transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '450ms' }}>
          <p className="text-white font-bold text-lg mb-2">{ctaCity ? `Serving ${ctaCity} & Surrounding Communities` : 'Don\'t see your city listed?'}</p>
          <p className="text-white/60 text-sm mb-5">Contact us — we may still serve your area, and we\'re always expanding our coverage throughout Greater Houston.</p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a href="tel:+17133382553" className="flex items-center gap-2 px-7 py-3 bg-[#C70532] text-white font-bold rounded-full hover:bg-[#a80428] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill" />
              (713) 338-2553
            </a>
            <Link to="/contact" className="px-7 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-full border border-white/20 transition-all cursor-pointer whitespace-nowrap">
              Check Your Area
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
